import { AxiosError } from 'axios';
import { compareAsc } from 'date-fns';
import startOfDay from 'date-fns/startOfDay';
import get from 'lodash.get';
import { put, call, takeLatest, select, delay } from 'redux-saga/effects';
import { API } from '../api/base';
import {
  types,
  EventDetails,
  Event,
  MembershipProduct,
  ApplyPriceTypePromoCodeAsyncRequest,
  ValidateMembershipResponse,
  ValidateMembershipAsyncRequest,
  EmailTicketsAsyncRequest,
  TextTicketsAsyncRequest,
  UpdatePrintCountRequest,
  UpsellStore
} from './types';
import { selectors as cartSelectors, Cart, PurchaseCartResponse } from 'services/cart';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as customerSelectors, types as customerTypes } from 'services/customer';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as cityPassSelectors, CityPassParams } from 'services/cityPass';
import { getErrorMessage } from 'services/api/normalizer';
import i18n from 'services/translation/i18n';

export function* createSessionAsync() {
  yield put({ type: types.CREATE_SESSION_ASYNC.PENDING, payload: true });

  try {
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);

    const sessionApi = new API(`ticketing/sessions`);
    const response: { session: string | null } = yield call(sessionApi.create, {
      cityPassParams
    });

    yield put({ type: types.CREATE_SESSION_ASYNC.SUCCESS, payload: response.session });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.CREATE_SESSION_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.CREATE_SESSION_ASYNC.PENDING, payload: false });
  }
}

export function* getEventsAsync() {
  yield put({ type: types.GET_EVENTS_ASYNC.PENDING, payload: true });

  try {
    const configuration: Configuration = yield select(configSelectors.selectConfiguration);
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const ticketingPath: string = yield select(ticketingSelectors.selectTicketingConfigPath);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const upsell: UpsellStore = yield select(ticketingSelectors.selectUpsell);
    const isLocal: boolean = yield select(customerSelectors.selectIsLocal);

    const eventsApi = new API(`ticketing/events?session=${session}`);
    const response: { events: Event[] } = yield call(eventsApi.create, {
      startDate: startOfDay(new Date()).toISOString(),
      cityPassParams,
      membershipParams,
      upsellParams: get(upsell, 'initialProduct.details.upsell.params'),
      isLocal
    });
    const sortedEvents = response.events.sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)));

    // add a "Donation" product if configured, but not for upsell
    const showDonation = get(configuration, `${ticketingPath}.donations.showInProductSelection`, false);
    const donationName = get(
      configuration,
      `${ticketingPath}.donations.productName.${i18n.language}`,
      i18n.t('donation.label')
    );
    if (showDonation && !upsell) {
      sortedEvents.unshift({
        id: 'donation',
        name: 'Donation',
        alias: donationName,
        genre: 'Donation',
        genreAlias: donationName,
        date: '',
        type: 'donation',
        detailsParams: {}
      });
    }

    yield put({ type: types.GET_EVENTS_ASYNC.SUCCESS, payload: sortedEvents });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.GET_EVENTS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.GET_EVENTS_ASYNC.PENDING, payload: false });
  }
}

export function* getEventDetailsAsync() {
  yield put({ type: types.GET_EVENTDETAILS_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const cityPassParams: CityPassParams = yield select(cityPassSelectors.selectAPIParams);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const event: Event = yield select(ticketingSelectors.selectEvent);
    const detailsParams = (event && event.detailsParams) || undefined;
    const isLocal: boolean = yield select(customerSelectors.selectIsLocal);
    const cart: Cart | null = yield select(cartSelectors.selectCart);

    const eventDetailsApi = new API(`ticketing/event?session=${session}`);
    const response: EventDetails = yield call(eventDetailsApi.create, {
      detailsParams,
      addMoreParams: cart && cart.addMoreParams ? cart.addMoreParams : undefined,
      cityPassParams,
      membershipParams,
      isLocal
    });

    yield put({ type: types.GET_EVENTDETAILS_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.GET_EVENTDETAILS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.GET_EVENTDETAILS_ASYNC.PENDING, payload: false });
  }
}

export function* getMembershipProductAsync() {
  yield put({ type: types.GET_MEMBERSHIP_PRODUCT_ASYNC.PENDING, payload: true });

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const { membershipParams } = yield select(ticketingSelectors.selectMembershipDetails);
    const event: Event = yield select(ticketingSelectors.selectEvent);
    const isLocal: boolean = yield select(customerSelectors.selectIsLocal);

    const detailsParams = (event && event.detailsParams) || undefined;

    const getMembershipApi = new API(`ticketing/membership?session=${session}`);
    const response: MembershipProduct = yield call(getMembershipApi.create, {
      detailsParams,
      membershipParams,
      isLocal
    });

    yield put({ type: types.GET_MEMBERSHIP_PRODUCT_ASYNC.SUCCESS, payload: response });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.GET_MEMBERSHIP_PRODUCT_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.GET_MEMBERSHIP_PRODUCT_ASYNC.PENDING, payload: false });
  }
}

export function* resetEventDataAsync() {
  yield put({ type: types.RESET_EVENT_DATA_ASYNC.PENDING, payload: true });

  try {
    const eventDataApi = new API(`ticketing`);

    yield call(eventDataApi.destroy, 'cache');
    yield put({ type: types.RESET_EVENT_DATA_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.RESET_EVENT_DATA_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.RESET_EVENT_DATA_ASYNC.PENDING, payload: false });
  }
}

export function* applyPriceTypePromoCodeAsync({ payload }: ApplyPriceTypePromoCodeAsyncRequest) {
  yield put({ type: types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.PENDING, payload: true });

  try {
    const event: EventDetails = yield select(ticketingSelectors.selectEventDetails);
    const session: string | null = yield select(ticketingSelectors.selectSession);
    const apiUrl = `ticketing/promoCode/priceType?session=${session}`;

    const api = new API(apiUrl);
    const response: EventDetails = yield call(api.create, { ...event.detailsParams, promoCode: payload });

    yield put({ type: types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.SUCCESS, payload: { ...response, promoCode: payload } });
  } catch (err) {
    let message = getErrorMessage(err as AxiosError);
    if (!message) message = 'Unknown error';

    yield put({ type: types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.PENDING, payload: false });
  }
}

export function* validateMembershipAsync({ payload }: ValidateMembershipAsyncRequest) {
  yield put({ type: types.VALIDATE_MEMBERSHIP_ASYNC.PENDING, payload: true });
  yield delay(3000);

  try {
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const api = new API(`ticketing/memberLogin?session=${session}`);
    const response: ValidateMembershipResponse = yield call(api.create, payload);
    const { customer, session: newSession, ...partialResponse } = response;

    yield put({ type: types.CREATE_SESSION_ASYNC.SUCCESS, payload: newSession });
    yield put({ type: customerTypes.SAVE_CUSTOMER_ASYNC.SUCCESS, payload: customer });
    yield delay(3000);

    yield put({ type: types.VALIDATE_MEMBERSHIP_ASYNC.SUCCESS, payload: partialResponse });
  } catch (err) {
    let message = getErrorMessage(err as AxiosError);
    if (!message) message = i18n.t('sagaError.validateMember');
    yield put({ type: types.VALIDATE_MEMBERSHIP_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.VALIDATE_MEMBERSHIP_ASYNC.PENDING, payload: false });
  }
}

export function* emailTicketsAsync({ payload }: EmailTicketsAsyncRequest) {
  yield put({ type: types.EMAIL_TICKETS_ASYNC.PENDING, payload: true });

  try {
    const deliveryEmail: string = yield select(ticketingSelectors.selectDeliveryEmail);
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const ticketsToEmailApi = new API(`ticketing/tickets/email?session=${session}`);
    yield call(ticketsToEmailApi.create, { email: deliveryEmail, ...payload });

    yield put({ type: types.EMAIL_TICKETS_ASYNC.SUCCESS, payload: 'sent' });
    yield delay(5000);
    yield put({ type: types.EMAIL_TICKETS_ASYNC.SUCCESS, payload: 'completed' });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.EMAIL_TICKETS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.EMAIL_TICKETS_ASYNC.PENDING, payload: false });
  }
}

export function* textTicketsAsync({ payload }: TextTicketsAsyncRequest) {
  yield put({ type: types.TEXT_TICKETS_ASYNC.PENDING, payload: true });

  try {
    const deliveryPhoneNumber: string = yield select(ticketingSelectors.selectDeliveryPhoneNumber);
    const session: string | null = yield select(ticketingSelectors.selectSession);

    const ticketsToTextApi = new API(`ticketing/tickets/text?session=${session}`);
    yield call(ticketsToTextApi.create, { phone: deliveryPhoneNumber, ...payload });

    yield put({ type: types.TEXT_TICKETS_ASYNC.SUCCESS, payload: 'completed' });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);

    yield put({ type: types.TEXT_TICKETS_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.TEXT_TICKETS_ASYNC.PENDING, payload: false });
  }
}

export function* updatePrintCountAsync({ payload }: UpdatePrintCountRequest) {
  yield put({ type: types.UPDATE_PRINT_COUNT_ASYNC.PENDING, payload: true });

  const checkoutCart: PurchaseCartResponse = yield select(cartSelectors.selectCheckoutCart);
  const session: string | null = yield select(ticketingSelectors.selectSession);

  try {
    const request = {
      buyerInfo: { images: [] },
      printCounts: [
        {
          barcode: payload.ticketNumber.split('_')[0],
          printedCount: payload.printCount,
          totalPrintableCount: payload.totalCount
        }
      ],
      transactionId: checkoutCart.orderId
    };
    const updatePrintApi = new API(`ticketing/tickets/printcount?session=${session}`);
    yield call(updatePrintApi.create, request);

    yield put({ type: types.UPDATE_PRINT_COUNT_ASYNC.SUCCESS });
  } catch (err) {
    const message = getErrorMessage(err as AxiosError);
    yield put({ type: types.UPDATE_PRINT_COUNT_ASYNC.ERROR, payload: message });
  } finally {
    yield put({ type: types.UPDATE_PRINT_COUNT_ASYNC.PENDING, payload: false });
  }
}

export const sagas = [
  takeLatest(types.CREATE_SESSION_ASYNC.REQUEST, createSessionAsync),
  takeLatest(types.GET_EVENTS_ASYNC.REQUEST, getEventsAsync),
  takeLatest(types.GET_EVENTDETAILS_ASYNC.REQUEST, getEventDetailsAsync),
  takeLatest(types.GET_MEMBERSHIP_PRODUCT_ASYNC.REQUEST, getMembershipProductAsync),
  takeLatest(types.RESET_EVENT_DATA_ASYNC.REQUEST, resetEventDataAsync),
  takeLatest(types.APPLY_PRICE_TYPE_PROMO_CODE_ASYNC.REQUEST, applyPriceTypePromoCodeAsync),
  takeLatest(types.VALIDATE_MEMBERSHIP_ASYNC.REQUEST, validateMembershipAsync),
  takeLatest(types.EMAIL_TICKETS_ASYNC.REQUEST, emailTicketsAsync),
  takeLatest(types.TEXT_TICKETS_ASYNC.REQUEST, textTicketsAsync),
  takeLatest(types.UPDATE_PRINT_COUNT_ASYNC.REQUEST, updatePrintCountAsync)
];
